import useHomeType from 'hooks/useHomeType'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  CapabilityToggleList,
  getVisibleCapabilityTogglesForRole
} from '../../../../components/generic/teamCapabilities'
import type {
  CapabilityKey,
  WizardRole
} from '../../../../components/generic/teamCapabilities/types'
import type { EditPermissionsData } from './types'

export type CapabilitySectionProps = {
  role: WizardRole
  capabilities: EditPermissionsData['capabilities']
  onChange: (key: CapabilityKey, next: boolean) => void
  testId?: string
  // Optional content rendered immediately after a given capability's card.
  renderAfter?: Partial<Record<CapabilityKey, ReactNode>>
  // Capability cards to render in the danger/error state.
  errorKeys?: CapabilityKey[]
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Heading = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: 0.4px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

export const CapabilitySection = ({
  role,
  capabilities,
  onChange,
  testId,
  renderAfter,
  errorKeys
}: CapabilitySectionProps) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  // CM-4109: hide Medication Management from services without eMAR.
  const { isEmar } = useHomeType()

  return (
    <Section data-testid={testId}>
      <Heading>{t(reportsConstants.EDIT_PANEL_CAPABILITIES_HEADING)}</Heading>
      <CapabilityToggleList
        toggles={getVisibleCapabilityTogglesForRole(role, { isEmar })}
        value={capabilities}
        onChange={onChange}
        testId={testId}
        renderAfter={renderAfter}
        errorKeys={errorKeys}
      />
    </Section>
  )
}
